import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import { teamAPI } from "@/api";
import TeamType from "@/constants/TeamType";
import TeamRoleType from "@/constants/TeamRoleType";
import TeamMemberStatusType from "@/constants/TeamMemberStatusType";
import cloneDeep from "lodash/cloneDeep";

import {
	TeamCreatedEvent,
	TeamMeetingAssignmentEvent,
	TeamMemberInvitedEvent
} from "@/telemetry";

function transformMembers( { members, personId, } ) {
	const hasSelectedActiveLead = !!members.find( m => m.role === TeamRoleType.LEAD && m.isRegisteredUser && m.inviteToTeam );
	if ( !hasSelectedActiveLead ) {
		const member = members.find( m => m.personId === personId );
		member.role = TeamRoleType.LEAD;
		member.inviteToTeam = true;
	}
	const activeSelectedLeadCount = members.reduce( ( accum, member ) => {
		if ( member.isRegisteredUser && member.role === TeamRoleType.LEAD && member.inviteToTeam ) {
			accum += 1;
		}
		return accum;
	}, 0 );
	const hasOnlyOneSelectedActiveLead = activeSelectedLeadCount === 1;
	members.forEach( m => {
		m.canEdit = hasOnlyOneSelectedActiveLead ?
			( !m.isRegisteredUser || ( m.isRegisteredUser && !( m.role === TeamRoleType.LEAD && m.inviteToTeam ) ) ) :
			true;
	} );
	return members;
}

const _state = {
	postTeamCreateCallback: () => {},
	meetingId: null,
	teamName: null,
	members: [
		/*
			example member:
			{
				personId: "8675309",
				email: "jenny@tutone.com",
				isRegisteredUser: true,
				role: "MEMBER", // or "LEAD"
				name: "Jenny Tutone",
				picture: "https://s.gravatar.com/avatar/12345678",
				inviteToTeam: true
			}
		*/
	],
	isDialogVisible: false,
	isValid: false,
	isSavingTeam: false,
};

const getters = {
	isDialogVisible: state => state.isDialogVisible,
	candidateTeamName: state => state.teamName,
	candidateTeamMembers: state => state.members,
	isValid: state => state.isValid,
	isSavingTeam: state => state.isSavingTeam,
};

const actions = {
	async initializeWithCandidateTeam( { commit, }, { meetingId, members, personId, postTeamCreateCallback, } ) {
		commit( "clearState" );
		commit( "candidateTeamDataLoaded", { meetingId, members, personId, postTeamCreateCallback, } );
		commit( "toggleDialogVisibility", true );
	},
	async saveTeamAndAssignToMeeting( { commit, state, }, { location, } ) {
		commit( "toggleIsSavingTeam", true );
		const teamPayload = {
			name: state.teamName,
			type: TeamType.TEAM,
			members: state.members.reduce( ( accum, member ) => {
				if ( member.inviteToTeam ) {
					accum.push( {
						email: member.email,
						personId: member.personId,
						role: member.role,
						status: member.isRegisteredUser ? TeamMemberStatusType.ACTIVE : TeamMemberStatusType.INVITED,
					} );
				}
				return accum;
			}, [] ),
		};

		try {
			Nprogress.start();

			const team = await teamAPI.createTeam( teamPayload );

			state.postTeamCreateCallback( team.id );
			commit( "toggleIsSavingTeam", false );
			commit( "clearState" );

			let telemetryEvent = new TeamCreatedEvent( team.name, TeamCreatedEvent.SCHEDULE_VIEW_LOCATION );
			telemetryEvent.submit();

			telemetryEvent = new TeamMeetingAssignmentEvent( team.name, TeamMeetingAssignmentEvent.SCHEDULE_LOCATION );
			telemetryEvent.submit();

			teamPayload.members.forEach( member => {
				telemetryEvent = new TeamMemberInvitedEvent( state.teamName, member.email, location );
				telemetryEvent.submit();
			} );
		} catch ( e ) {
			VueNotifications.error( {
				message: e.message || e || "Unexpected error",
			} );
			console.error( e );
		}
		Nprogress.done();
	},
};

/**
 * Reactions are really just mutations that are called when an
 *   action invokes the store's `commit()` function. Separating them
 *   here for clarity. They are merged into mutations below.
 */
const reactions = {
	candidateTeamDataLoaded( state, { meetingId, members, personId, postTeamCreateCallback, } ) {
		state.meetingId = meetingId;
		state.currentPersonId = personId;
		state.members = [ ...transformMembers( { members, personId, } ), ];
		state.postTeamCreateCallback = postTeamCreateCallback;
	},
	clearState( state ) {
		state.meetingId = null;
		state.members = [];
		state.teamName = null;
		state.postTeamCreateCallback = () => {};
	},
	toggleDialogVisibility( state, isVisible ) {
		state.isDialogVisible = isVisible;
	},
	toggleIsSavingTeam( state, isSaving ) {
		state.isSavingTeam = isSaving;
	},
	updateMembers( state, { members, } ) {
		state.members = [ ...transformMembers( { members, personId: state.currentPersonId, } ), ];
	},
};

const mutations = {
	...reactions,
	updateMemberRole( state, { personId, role, } ) {
		const members = cloneDeep( state.members );
		const member = members.find( x => x.personId === personId );
		if ( member ) {
			member.role = role;
			state.members = [ ...transformMembers( { members, personId: state.currentPersonId, } ), ];
		}
	},
	updateTeamName( state, { teamName, } ) {
		state.teamName = teamName;
	},
	toggleInviteStatus( state, { personId, } ) {
		const members = cloneDeep( state.members );
		const member = members.find( x => x.personId === personId );
		if ( member ) {
			member.inviteToTeam = !member.inviteToTeam;
			state.members = [ ...transformMembers( { members, personId: state.currentPersonId, } ), ];
		}
	},
};

export default {
	state: _state,
	getters,
	actions,
	mutations,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/teamCreationModal/index.js